import { Link, useParams } from 'react-router-dom'
import { TrendingUp, Truck, Package, Check, PackageCheck, Phone, MessageCircle, AlertCircle, RefreshCw, Box } from 'lucide-react'
import { usePurchaseOrder } from '@/hooks/procurement'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'

function PurchaseLogistics() {
  const { id } = useParams()
  const orderId = id ? parseInt(id) : 0

  const { data: purchaseOrder, isLoading, error } = usePurchaseOrder(orderId)

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载物流信息..." />
      </div>
    )
  }

  if (error || !purchaseOrder) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  // Generate mock timeline based on purchase order status
  const generateTimeline = () => {
    const createdAt = new Date(purchaseOrder.createdAt)
    const timeline = []

    // Created
    timeline.push({
      status: '已创建',
      time: `${formatDateTime(createdAt)}`,
      desc: '采购单已创建，等待审批',
      icon: '✓',
      active: false,
    })

    // Approved (if past created status)
    if (
      purchaseOrder.status === 'APPROVED' ||
      purchaseOrder.status === 'ORDERED' ||
      purchaseOrder.status === 'IN_TRANSIT' ||
      purchaseOrder.status === 'PARTIALLY_RECEIVED' ||
      purchaseOrder.status === 'RECEIVED' ||
      purchaseOrder.status === 'CLOSED'
    ) {
      const approvedTime = new Date(createdAt.getTime() + 60 * 60 * 1000) // Mock: +1 hour
      timeline.unshift({
        status: '已审批',
        time: `${formatDateTime(approvedTime)}`,
        desc: '采购单已通过审批',
        icon: '✓',
        active: false,
      })
    }

    // Ordered
    if (
      purchaseOrder.status === 'ORDERED' ||
      purchaseOrder.status === 'IN_TRANSIT' ||
      purchaseOrder.status === 'PARTIALLY_RECEIVED' ||
      purchaseOrder.status === 'RECEIVED' ||
      purchaseOrder.status === 'CLOSED'
    ) {
      const orderedTime = new Date(createdAt.getTime() + 2 * 60 * 60 * 1000) // Mock: +2 hours
      timeline.unshift({
        status: '已下单',
        time: `${formatDateTime(orderedTime)}`,
        desc: `已向${purchaseOrder.supplierName}下达采购订单`,
        icon: '📦',
        active: false,
      })
    }

    // Shipped / In Transit
    if (
      purchaseOrder.status === 'IN_TRANSIT' ||
      purchaseOrder.status === 'PARTIALLY_RECEIVED' ||
      purchaseOrder.status === 'RECEIVED' ||
      purchaseOrder.status === 'CLOSED'
    ) {
      const shippedTime = new Date(createdAt.getTime() + 4 * 60 * 60 * 1000) // Mock: +4 hours
      timeline.unshift({
        status: '运输中 - 已发货',
        time: `${formatDateTime(shippedTime)}`,
        desc: `${purchaseOrder.supplierName}已发货，物流单号: ${purchaseOrder.trackingNo || '暂无'}`,
        icon: '🚚',
        active: purchaseOrder.status === 'IN_TRANSIT',
      })
    }

    // Received
    if (
      purchaseOrder.status === 'RECEIVED' ||
      purchaseOrder.status === 'CLOSED'
    ) {
      const receivedTime = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000) // Mock: +1 day
      timeline.unshift({
        status: '已到货',
        time: `${formatDateTime(receivedTime)}`,
        desc: '配件已全部到货，等待入库',
        icon: '✓',
        active: false,
      })
    }

    return timeline.reverse()
  }

  const timeline = generateTimeline()

  function formatDateTime(date: Date): string {
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Calculate estimated arrival (mock)
  const createdAt = new Date(purchaseOrder.createdAt)
  const expectedArrival = new Date(createdAt.getTime() + 3 * 24 * 60 * 60 * 1000) // +3 days

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/supply-chain/purchase/${id}`} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <TrendingUp className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">物流跟踪</h1>
              <p className="text-sm text-gray-500 mt-1">{purchaseOrder.purchaseNo}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              刷新物流
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Logistics Timeline */}
        <div className="col-span-2 space-y-6">
          {/* Shipping Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">物流信息</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-purple-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">物流公司</div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-purple-600" />
                    顺丰速运
                  </div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">物流单号</div>
                  <div className="font-bold text-gray-900">{purchaseOrder.trackingNo || '暂无'}</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">发货时间</div>
                  <div className="font-bold text-gray-900">
                    {formatDateTime(createdAt)}
                  </div>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-1">预计到达</div>
                  <div className="font-bold text-amber-600">
                    {formatDateTime(expectedArrival)}
                  </div>
                </div>
              </div>

              {purchaseOrder.status === 'IN_TRANSIT' && (
                <div className="p-4 bg-blue-50 rounded-xl flex items-center gap-3">
                  <Box className="w-5 h-5 text-blue-600" />
                  <div className="text-sm text-blue-800">
                    <span className="font-medium">运输中</span> · 配件正在从供应商发往仓库，预计{' '}
                    {Math.ceil((expectedArrival.getTime() - Date.now()) / (24 * 60 * 60 * 1000))}{' '}
                    天到达
                  </div>
                </div>
              )}

              {(purchaseOrder.status === 'RECEIVED' || purchaseOrder.status === 'CLOSED') && (
                <div className="p-4 bg-green-50 rounded-xl flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <div className="text-sm text-green-800">
                    <span className="font-medium">已到货</span> · 配件已全部到货并入库
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">物流动态</h2>
              <span className="text-sm text-gray-500">更新于 {formatDateTime(new Date())}</span>
            </div>
            <div className="p-5">
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-[16px] top-10 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Timeline Items */}
                <div className="space-y-6">
                  {timeline.map((item, index) => (
                    <div key={index} className="relative flex gap-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center z-10 flex-shrink-0 ${
                          item.active
                            ? 'bg-purple-500'
                            : index === 0
                            ? 'bg-green-500'
                            : 'bg-blue-500'
                        } text-white`}
                      >
                        <span className="text-sm">{item.icon}</span>
                      </div>
                      <div
                        className={`flex-1 rounded-xl p-4 ${item.active ? 'bg-purple-50' : ''}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-gray-900">{item.status}</span>
                          <span className="text-xs text-gray-500">{item.time}</span>
                        </div>
                        <div className="text-sm text-gray-600">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-6">
          {/* Purchase Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">采购单信息</h2>
            </div>
            <div className="p-5">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">采购单号</span>
                  <span className="font-medium text-primary-600">{purchaseOrder.purchaseNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">供应商</span>
                  <span className="font-medium text-gray-900">{purchaseOrder.supplierName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">配件数量</span>
                  <span className="font-medium text-gray-900">{purchaseOrder.items.length} 项</span>
                </div>
                {purchaseOrder.workOrderNo && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">关联工单</span>
                    <span className="font-medium text-gray-900">{purchaseOrder.workOrderNo}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Parts in Transit */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">在途配件</h2>
            </div>
            <div className="p-5 space-y-3">
              {purchaseOrder.items.map((item: any, index: number) => {
                const emojis = ['⚙️', '🛢️', '🔧', '📦', '💡']
                const icon = emojis[index % emojis.length]

                return (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-lg">
                      {icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{item.partName}</div>
                      <div className="text-xs text-gray-500">
                        ×{item.quantity} {item.unit}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">快捷操作</h2>
            </div>
            <div className="p-5 space-y-2">
              {purchaseOrder.status === 'IN_TRANSIT' && (
                <button className="w-full px-4 py-3 bg-primary-50 hover:bg-primary-100 rounded-xl text-sm text-primary-700 text-left flex items-center gap-3 transition">
                  <PackageCheck className="w-5 h-5 text-primary-600" />
                  确认收货
                </button>
              )}
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <Phone className="w-5 h-5 text-gray-500" />
                联系快递员
              </button>
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <MessageCircle className="w-5 h-5 text-gray-500" />
                发送催件短信
              </button>
              <button className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 text-left flex items-center gap-3 transition">
                <AlertCircle className="w-5 h-5 text-gray-500" />
                异常上报
              </button>
            </div>
          </div>

          {/* Contact Card */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold">供应商客服</div>
                <div className="text-xs opacity-80">{purchaseOrder.supplierName}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition">
                拨打电话
              </button>
              <button className="flex-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition">
                在线客服
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseLogistics
